import React from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { apiUrl, getApiBase } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";

function mediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${getApiBase()}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

function isPhotoPost(post) {
  return String(post?.mediaType || "").toLowerCase() === "image" || Boolean(post?.imageUrl);
}

function isTextPost(post) {
  return String(post?.mediaType || "").toLowerCase() === "text";
}

export default function NativeFeedMediaEnhancer() {
  const location = useLocation();
  const [posts, setPosts] = React.useState([]);
  const [lightbox, setLightbox] = React.useState(null);

  const active = String(location.pathname || "") === "/feed";

  const load = React.useCallback(async () => {
    if (!active) return;
    try {
      const token = getAuthToken();
      const response = await fetch(apiUrl("/posts"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch {}
  }, [active]);

  React.useEffect(() => {
    load();
    const onCreated = () => load();
    window.addEventListener("ga:photo-post-created", onCreated);
    window.addEventListener("ga:feed-post-created", onCreated);
    window.addEventListener("auth:changed", onCreated);
    return () => {
      window.removeEventListener("ga:photo-post-created", onCreated);
      window.removeEventListener("ga:feed-post-created", onCreated);
      window.removeEventListener("auth:changed", onCreated);
    };
  }, [load]);

  React.useEffect(() => {
    if (!active || !posts.length) return undefined;

    const sync = () => {
      for (const post of posts.filter((item) => isPhotoPost(item) || isTextPost(item))) {
        const article = document.getElementById(`post-${post.id}`);
        if (!article) continue;
        article.classList.add("ga-enhanced-feed-post");

        const content = article.children?.[1];
        if (!(content instanceof HTMLElement)) continue;

        const placeholder = Array.from(content.querySelectorAll("div")).find(
          (element) => String(element.textContent || "").trim() === "Missing playbackId for this post."
        );
        if (placeholder instanceof HTMLElement) placeholder.style.display = "none";

        if (isPhotoPost(post)) {
          const selector = `[data-ga-feed-photo="${CSS.escape(String(post.id))}"]`;
          if (content.querySelector(selector)) continue;
          const src = mediaUrl(post.imageUrl);
          if (!src) continue;

          const button = document.createElement("button");
          button.type = "button";
          button.className = "ga-native-feed-photo";
          button.dataset.gaFeedPhoto = String(post.id);
          button.setAttribute("aria-label", `Open ${post.caption || "photo"} larger`);

          const image = document.createElement("img");
          image.src = src;
          image.alt = String(post.caption || "GAStreaming photo");
          image.loading = "lazy";
          button.appendChild(image);

          const expand = document.createElement("span");
          expand.textContent = "⛶ View larger";
          button.appendChild(expand);

          button.addEventListener("click", () => setLightbox({ ...post, src }));
          content.insertBefore(button, placeholder || content.firstChild);
          continue;
        }

        if (isTextPost(post)) {
          const selector = `[data-ga-feed-text="${CSS.escape(String(post.id))}"]`;
          if (content.querySelector(selector)) continue;
          const body = document.createElement("div");
          body.dataset.gaFeedText = String(post.id);
          body.textContent = String(post.caption || "");
          Object.assign(body.style, {
            margin: "2px auto 4px",
            maxWidth: "680px",
            padding: "18px 16px",
            borderRadius: "14px",
            border: "1px solid rgba(255,255,255,.1)",
            background: "linear-gradient(145deg, rgba(68,8,20,.5), rgba(10,10,14,.72))",
            color: "rgba(255,255,255,.94)",
            fontSize: "clamp(17px, 2.7vw, 22px)",
            lineHeight: "1.55",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          });
          content.insertBefore(body, placeholder || content.firstChild);
        }
      }
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.getElementById("root") || document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [active, posts]);

  React.useEffect(() => {
    if (!lightbox) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [lightbox]);

  if (!active || !lightbox || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="ga-native-media-lightbox"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setLightbox(null);
      }}
    >
      <section role="dialog" aria-modal="true" aria-label="Photo viewer">
        <header>
          <div>
            <strong>{lightbox.caption || "Photo post"}</strong>
            <small>@{lightbox.username || lightbox.displayName || "member"}</small>
          </div>
          <button type="button" onClick={() => setLightbox(null)} aria-label="Close photo viewer">
            ×
          </button>
        </header>
        <img src={lightbox.src} alt={lightbox.caption || "GAStreaming photo"} />
      </section>
    </div>,
    document.body
  );
}